import React from "react";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CircularProgress from "@material-ui/core/CircularProgress";
import cn from "classnames";
import PropTypes from "prop-types";
import withStyles from "components/with-styles";
import Footer from "./partial/footer";
import styles from "./styles/paginated-table.css.js";
import TablePagination from "./partial/table-pagination";
import NoData from "./no-data";
import { FormattedMessage } from "react-intl";

const PaginatedTable = ({
  paginatedElements: { items, total },
  headerComponent: Header,
  rowComponent: Row = TableRow,
  rowProps,
  className,
  classes,
  footer,
  getItemKey,
  noDataMessage,
  pageName,
  refetchData,
  showPagination = true,
  loading = false,
  error = null,
  ...props
}) => {
  if (!loading && !error && total === 0) {
    return <NoData noDataMessage={noDataMessage} />;
  }

  return (
    <Paper elevation={2} className={cn(classes.paginatedtable, className)}>
      <Table>
        <TableHead>
          <Header {...rowProps} />
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={100} align="center" style={{ padding: "40px" }}>
                <CircularProgress />
                <div style={{ marginTop: "16px" }}>
                  <FormattedMessage id="common.loading" />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={100} align="center" style={{ padding: "40px", color: "#f44336" }}>
                <FormattedMessage id="common.graphql.error" />
              </TableCell>
            </TableRow>
          ) : (
            <>
              {items?.map((item, index) => (
                <Row
                  key={getItemKey ? getItemKey(item, index) : item._id ?? item.id ?? index}
                  item={item}
                  refetchData={refetchData}
                  {...rowProps}
                />
              ))}
              {Array.isArray(items) && !items.length && (
                <TableRow>
                  <TableCell />
                </TableRow>
              )}
            </>
          )}
        </TableBody>
        <TablePagination pageName={pageName} show={showPagination && !loading} total={total} {...props} />
      </Table>
      <Footer>{footer}</Footer>
    </Paper>
  );
};

PaginatedTable.propTypes = {
  /**
   * Clase adicional del componente.
   */
  className: PropTypes.string,
  /**
   * Elementos paginados en un objeto con la colección y el total
   **/
  paginatedElements: PropTypes.object.isRequired,
  /**
   * Página de la tabla que se está mostrando
   **/
  page: PropTypes.number,
  /**
   * Número de elementos por página
   **/
  perPage: PropTypes.number.isRequired,
  /**
   * Handler del cambio de página
   **/
  onChangePage: PropTypes.func,
  /**
   * Handler del cambio de elementos por página
   **/
  onChangePerPage: PropTypes.func,
  /**
   * Componente de la cabecera de la tabla
   **/
  headerComponent: PropTypes.elementType,
  /**
   * Muestra o no los controles de paginación
   **/
  showPagination: PropTypes.bool,
  /**
   * Footer opcional de tabla
   **/
  footer: PropTypes.node,
  /**
   * Componente que renderiza una fila de la tabla a partir de un elemento
   **/
  rowComponent: PropTypes.elementType.isRequired,
  /**
   * Props adicionales para el componente de fila
   **/
  rowProps: PropTypes.object,
  /**
   * Devuelve una key válida para que React identifique este elemento en las iteraciones
   * Por defecto, el "id" (item.id)
   **/
  getItemKey: PropTypes.func,
  /**
   * Función de refetch de datos
   */
  refetchData: PropTypes.func,
  /**
   * Nombre del parámetro de query en la url que representa el paginado de esta tabla
   **/
  pageName: PropTypes.string,
  /**
   * Indica si la tabla está cargando datos
   **/
  loading: PropTypes.bool,
  /**
   * Error al cargar los datos
   **/
  error: PropTypes.object,
};

export default withStyles(styles)(PaginatedTable);
